import { useEffect, useRef, useState } from 'react';

import WebViewer from '@pdftron/webviewer';

import saveIconDisabled from 'assets/images/saveDisabled.svg';
import saveIconEnabled from 'assets/images/saveEnabled.svg';
import {
  computePinPositionFromClick,
  createAnnotationsVisibilityButton,
  createItemVisibilityButton,
  createQrCodeButton,
  createSaveButton,
  displayFirstPageOnly,
  onViewerScroll,
} from 'components/PdfViewer/utils';
import config from 'config/config';
import useCurrentUser from 'hooks/useCurrentUser';
import globalMessages from 'translations/messages/global-messages';
import localStorageUser from 'utils/localStorageUser';
import { reportPdftronException } from 'utils/pdftron.utils';
import getUserFullName from 'utils/users';

const redColorRGB = { r: 233, g: 79, b: 61 };
const blueColorRGB = { r: 76, g: 171, b: 242 };
const greenColorRGB = { r: 73, g: 203, b: 12 };
const blackColorRGB = { r: 0, g: 0, b: 0 };

const MEASUREMENT_TOOL_BASES = [
  'AnnotationCreateDistanceMeasurement',
  'AnnotationCreateAreaMeasurement',
  'AnnotationCreateRectangularAreaMeasurement',
  'AnnotationCreateEllipseMeasurement',
];

const MEASUREMENT_TOOL_COLORS = [
  { suffix: '', color: redColorRGB },
  { suffix: '2', color: blueColorRGB },
  { suffix: '3', color: blackColorRGB },
  { suffix: '4', color: greenColorRGB },
];

const applyToolMeasurementStyle = (tool, instance, isDocument, color) => {
  const scale = isDocument ? 100 : 1;
  tool.setStyles({
    Scale: [[1, 'm'], [scale, 'm']],
    StrokeColor: new instance.Core.Annotations.Color(color.r, color.g, color.b, 0.8),
    FillColor: new instance.Core.Annotations.Color(color.r, color.g, color.b, 0.3),
    StrokeThickness: 1,
  });
};

const applyToolsStyle = (instance, isDocument) => {
  const { documentViewer } = instance.Core;
  MEASUREMENT_TOOL_COLORS.forEach(({ suffix, color }) => {
    MEASUREMENT_TOOL_BASES.forEach(baseName => {
      const tool = documentViewer.getTool(`${baseName}${suffix}`);
      applyToolMeasurementStyle(tool, instance, isDocument, color);
    });
  });
};

/**
 * This hook owns the imperative lifecycle of the PDFTron full
 * WebViewer used by the plan/document viewer screen. It only runs its
 * initialisation effect once, so it reads dynamic values through the
 * `latestPropsRef` (refreshed by the caller on every render) to avoid stale
 * closures.
 */
const usePdfTronViewer = ({
  mountRef,
  intl,
  tronId,
  docUrl,
  addZoomButton,
  pdfDocument,
  userInfo,
  initialTools,
  latestPropsRef,
  setters: {
    setAnnotationSelected,
    setIsAnnotating,
    setZoomUpdated,
    setNeedUpdateAnnotations,
    setTriggerClickToPlacePin,
  },
  onSaveDocument,
}) => {
  const { userId: currentUserId } = useCurrentUser();
  const [pdfTronInstance, setPdfTronInstance] = useState();
  const canSaveRef = useRef(false);
  const isInitializingRef = useRef(false);
  const isClickToPlacePinModeRef = useRef(false);

  useEffect(() => {
    if (isInitializingRef.current) return undefined;

    let instanceToDispose = null;
    let detachViewerListeners = () => {};
    let cancelled = false;

    (async () => {
      try {
        if (cancelled) return;
        isInitializingRef.current = true;

        const instance = await WebViewer(
          {
            path: '/webviewer',
            licenseKey: config.pdfTronLicenseKey,
            forceClientSideInit: true,
            fullAPI: true,
            backendType: 'ems',
            disabledElements: [
              'leftPanelButton',
              'menuButton',
              'viewControlsButton',
              'annotationPopup',
              'contextMenuPopup',
              'stickyToolGroupButton',
              ...(!addZoomButton ? ['zoomOverlayButton'] : []),
            ],
          },
          mountRef.current,
        );

        instanceToDispose = instance;
        if (instance.Core.PDFNet) {
          try {
            await instance.Core.PDFNet.initialize(config.pdfTronLicenseKey);
          } catch (err) {
            // PDFNet is optional; failure here only disables advanced features.
            // eslint-disable-next-line no-console
            console.error('PDFNet initialization error:', err);
          }
        }

        const { annotationManager, documentViewer } = instance.Core;
        const documentViewerUI = instance.UI;
        const viewerEl = documentViewer.getViewerElement();
        const language = localStorageUser.getLanguageUserFromLocalStorage();
        const userStore = localStorageUser.getUserStore();

        annotationManager.setCurrentUser(getUserFullName(userInfo || userStore[currentUserId]));
        applyToolsStyle(instance, !!pdfDocument);

        const disableSaveButton = () => {
          canSaveRef.current = false;
          documentViewerUI.updateElement('saveButton', { img: saveIconDisabled });
        };

        const enableSaveButton = () => {
          canSaveRef.current = true;
          documentViewerUI.updateElement('saveButton', { img: saveIconEnabled });
        };

        const handleAnnotationChanged = (_info, _action, { imported }) => {
          if (imported) return;
          enableSaveButton();
          setAnnotationSelected(true);
        };

        const handleViewerWheel = e => onViewerScroll(e, documentViewer);

        instance.UI.setLanguage(language);
        documentViewerUI.disableFeatures(Object.values(documentViewerUI.Feature));
        documentViewerUI.enableFeatures(documentViewerUI.Feature.Annotations);
        documentViewerUI.enableFeatures(documentViewerUI.Feature.Measurement);
        documentViewer.setToolMode(documentViewer.getTool(instance.Core.Tools.ToolNames.PAN));

        const handleToolModeUpdated = tool => {
          setIsAnnotating(tool.name.startsWith('AnnotationCreate'));
        };
        const handleZoomUpdated = () => setZoomUpdated(true);
        const handleBeginRendering = () => setNeedUpdateAnnotations(false);
        const handleFinishedRendering = () => setNeedUpdateAnnotations(true);

        documentViewer.addEventListener('toolModeUpdated', handleToolModeUpdated);
        documentViewer.addEventListener('zoomUpdated', handleZoomUpdated);
        documentViewer.addEventListener('beginRendering', handleBeginRendering);
        documentViewer.addEventListener('finishedRendering', handleFinishedRendering);

        const handleViewerClick = e => {
          if (!isClickToPlacePinModeRef.current) return;
          const position = computePinPositionFromClick(documentViewer, e);
          if (position) setTriggerClickToPlacePin(position);
        };
        viewerEl.addEventListener('click', handleViewerClick);

        const handleDocumentLoaded = () => {
          const {
            isFirstPageOnly: latestIsFirstPageOnly,
            changeLoader: latestChangeLoader,
            onPageLoadSuccess: latestOnPageLoadSuccess,
          } = latestPropsRef.current;

          if (latestIsFirstPageOnly) displayFirstPageOnly(documentViewer);
          if (latestChangeLoader) latestChangeLoader(false);

          const rotation = documentViewer.getCompleteRotation(1);
          latestOnPageLoadSuccess(rotation);

          // Compute a "fit page" zoom once, then lock it to avoid v11
          // auto-resize loops on subsequent renders.
          const applyInitialFitPageAndLockZoom = () => {
            documentViewer.removeEventListener('finishedRendering', applyInitialFitPageAndLockZoom);
            documentViewer.setFitMode(documentViewer.FitMode.FitPage);
            requestAnimationFrame(() => {
              const fitPageZoom = documentViewer.getZoomLevel();
              documentViewer.setFitMode(documentViewer.FitMode.Zoom);
              documentViewer.zoomTo(fitPageZoom);
            });
          };
          documentViewer.addEventListener('finishedRendering', applyInitialFitPageAndLockZoom);

          // Re-register listeners on each load to avoid duplicate handlers.
          annotationManager.removeEventListener('annotationChanged', handleAnnotationChanged);
          annotationManager.addEventListener('annotationChanged', handleAnnotationChanged);
          viewerEl.removeEventListener('wheel', handleViewerWheel, { passive: false });
          viewerEl.addEventListener('wheel', handleViewerWheel, { passive: false });

          documentViewerUI.setNotesPanelSortStrategy(
            instance.UI.NotesPanelSortStrategy.CREATED_DATE,
          );

          instance.UI.setToolMode(instance.Core.Tools.ToolNames.PAN);
        };

        const handleDocumentLoadFailed = () => {
          const {
            onPageLoadError: latestOnPageLoadError,
            changeLoader: latestChangeLoader,
          } = latestPropsRef.current;
          reportPdftronException(
            new Error('WebViewer documentLoadFailed event'),
            'FULL_DOCUMENT_LOAD_EVENT_FAILED',
            { tron_id: tronId, doc_url: docUrl },
          );
          if (latestOnPageLoadError) latestOnPageLoadError();
          if (latestChangeLoader) latestChangeLoader(false);
        };

        documentViewer.addEventListener('documentLoaded', handleDocumentLoaded);
        documentViewer.addEventListener('documentLoadFailed', handleDocumentLoadFailed);

        const qrCodeButton = createQrCodeButton({
          instance,
          title: intl.formatMessage(globalMessages.qrcode),
        });
        const itemVisibilityButton = createItemVisibilityButton({
          instance,
          intl,
          messages: globalMessages,
          initiallyVisible: initialTools.itemsVisibilityOptionEnabled,
          onToggle: () => latestPropsRef.current.tools.toggleItemsVisibility(),
        });
        const annotationsVisibilityButton = createAnnotationsVisibilityButton({
          instance,
          intl,
          messages: globalMessages,
          initiallyVisible: initialTools.annotationAreVisible,
          onToggle: () => latestPropsRef.current.tools.toggleAnnotationVisibility(),
        });
        const saveButton = createSaveButton({
          instance,
          title: intl.formatMessage(globalMessages.save),
          onClick: () => {
            if (!canSaveRef.current) return;
            onSaveDocument({ instance, disableSaveButton });
            setIsAnnotating(false);
          },
        });

        instance.UI.enableFeatures([instance.UI.Feature.LocalStorage]);
        const defaultHeader = instance.UI.getModularHeader('default-top-header');
        const customItems = [qrCodeButton];
        if (initialTools.itemsVisibilityOptionEnabled) customItems.push(itemVisibilityButton);
        if (initialTools.annotationVisibilityOptionEnabled) {
          customItems.push(annotationsVisibilityButton);
        }
        customItems.push(saveButton);
        defaultHeader.setItems([...defaultHeader.getItems(), ...customItems]);

        disableSaveButton();

        detachViewerListeners = () => {
          viewerEl.removeEventListener('click', handleViewerClick);
          viewerEl.removeEventListener('wheel', handleViewerWheel);
          annotationManager.removeEventListener('annotationChanged', handleAnnotationChanged);
          documentViewer.removeEventListener('documentLoaded', handleDocumentLoaded);
          documentViewer.removeEventListener('documentLoadFailed', handleDocumentLoadFailed);
          documentViewer.removeEventListener('toolModeUpdated', handleToolModeUpdated);
          documentViewer.removeEventListener('zoomUpdated', handleZoomUpdated);
          documentViewer.removeEventListener('beginRendering', handleBeginRendering);
          documentViewer.removeEventListener('finishedRendering', handleFinishedRendering);
        };

        if (cancelled) return;
        setPdfTronInstance(instance);
      } catch (err) {
        reportPdftronException(err, 'FULL_VIEWER_INIT_FAILED', {
          tron_id: tronId,
          doc_url: docUrl,
        });
        const {
          onPageLoadError: latestOnPageLoadError,
          changeLoader: latestChangeLoader,
        } = latestPropsRef.current;
        if (latestOnPageLoadError) latestOnPageLoadError(err);
        if (latestChangeLoader) latestChangeLoader(false);
      }
    })();

    return () => {
      cancelled = true;
      detachViewerListeners();
      if (instanceToDispose) {
        instanceToDispose.UI.dispose();
      }
      isInitializingRef.current = false;
    };
    // Init must only run once; subsequent prop changes are routed through latestPropsRef.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { pdfTronInstance, canSaveRef, isClickToPlacePinModeRef };
};

export default usePdfTronViewer;
